import { Button, SegmentedControl, Select, TextInput, Tooltip } from "@mantine/core";
import {
  IconAlertTriangle,
  IconAward,
  IconBan,
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconCircleOff,
  IconHeartHandshake,
  IconInfoCircle,
  IconTable,
  IconTarget,
  IconTrophy,
} from "@tabler/icons-react";
import {
  type CompareResult,
  type CompareSettings,
  computeCompare,
  fmtMoney,
  fmtUnitPrice,
  resolveWeight,
} from "@/lib/rfq/calc";
import { Fragment, useMemo, useState } from "react";
import type {
  RfqAward,
  RfqLine,
  RfqQuote,
  RfqQuoteMeta,
  RfqRole,
  RfqStatus,
  RfqVendor,
} from "@/lib/rfq/types";

import { AWARD_REASON_KEYS } from "@/lib/rfq/constants";
import { RfqSection } from "./RfqSection";
import { useRfqLabels } from "@/lib/rfq/labels";

type RankKind = "best" | "close" | "over" | "warn" | "none";

interface Cell {
  quote?: RfqQuote;
  prevQuote?: RfqQuote;
  negotiatedPct: number | null;
  result?: CompareResult;
  rank: RankKind;
  deltaPct: number | null;
  vsTargetPct: number | null;
}

const RANK_STYLE: Record<RankKind, string> = {
  best: "bg-emerald-50 text-emerald-700",
  close: "bg-sky-50 text-sky-700",
  over: "bg-red-50 text-red-600",
  warn: "bg-gray-50 text-gray-600",
  none: "",
};

const RANK_DOT: Record<RankKind, string> = {
  best: "bg-emerald-200",
  close: "bg-sky-200",
  over: "bg-red-200",
  warn: "bg-gray-200",
  none: "bg-gray-100",
};

function LegendDot({ kind, label }: { kind: RankKind; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-3 w-3 rounded ${RANK_DOT[kind]}`} />
      {label}
    </span>
  );
}

function SummaryStat({
  label,
  value,
  strong,
  tone,
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: "good" | "bad";
}) {
  const toneClass =
    tone === "good"
      ? "text-emerald-600"
      : tone === "bad"
        ? "text-red-500"
        : "text-gray-700";
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-gray-500">{label}</span>
      <span
        className={`tabular-nums ${strong ? "text-lg font-bold" : "text-base font-semibold"} ${toneClass}`}
      >
        {value}
      </span>
    </div>
  );
}

function PriceCell({
  cell,
  baseUnit,
  showTarget,
}: {
  cell?: Cell;
  baseUnit: string;
  showTarget: boolean;
}) {
  const { t } = useRfqLabels();

  if (!cell || !cell.quote) {
    return (
      <td className="border-b border-gray-100 px-3 py-2.5 text-right align-top text-xs text-gray-400">
        —
      </td>
    );
  }
  if (!cell.quote.supplied) {
    return (
      <td className="border-b border-gray-100 px-3 py-2.5 text-right align-top">
        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
          <IconCircleOff size={14} /> {t("rfq.pricing.missing") || "缺項"}
        </span>
      </td>
    );
  }

  const result = cell.result as CompareResult;

  return (
    <td className="border-b border-gray-100 px-2 py-2 align-top">
      <div
        className={`flex flex-col items-end gap-1 rounded-lg px-2 py-1.5 ${RANK_STYLE[cell.rank]}`}
      >
        <div className="flex items-center gap-1.5">
          {cell.rank === "best" ? <IconTrophy size={14} /> : null}
          {result.comparable && result.base != null ? (
            <Tooltip
              multiline
              w={340}
              withArrow
              label={
                <span className="whitespace-pre-line">{result.formula}</span>
              }
            >
              <span className="inline-flex cursor-help items-center gap-1">
                <span className="text-sm font-semibold tabular-nums">
                  {fmtUnitPrice(result.base)}
                </span>
                <IconInfoCircle size={12} className="opacity-60" />
              </span>
            </Tooltip>
          ) : (
            <Tooltip
              multiline
              w={340}
              withArrow
              label={
                <span className="whitespace-pre-line">{result.formula}</span>
              }
            >
              <span className="inline-flex cursor-help items-center gap-1 text-xs text-red-500">
                <IconAlertTriangle size={14} />
                {t("rfq.pricing.cannotCompare") || "無法比價"}
              </span>
            </Tooltip>
          )}
        </div>
        {result.comparable && result.base != null ? (
          <span className="text-[10px] opacity-70">{baseUnit}</span>
        ) : null}
        {cell.negotiatedPct != null &&
        cell.negotiatedPct < 0 &&
        cell.prevQuote ? (
          <Tooltip
            withArrow
            label={t("rfq.pricing.negotiatedFrom", {
              defaultValue:
                "議價前 {{from}} 元/{{fromUnit}} → 現 {{to}} 元/{{toUnit}}",
              from: fmtUnitPrice(cell.prevQuote.unitPrice),
              fromUnit: cell.prevQuote.quoteUnit,
              to: fmtUnitPrice((cell.quote as RfqQuote).unitPrice),
              toUnit: (cell.quote as RfqQuote).quoteUnit,
            })}
          >
            <span className="inline-flex cursor-help items-center gap-1 rounded bg-emerald-100/70 px-1 text-[10px] font-medium text-emerald-700">
              {t("rfq.pricing.negotiatedTag") || "議價"} ↓
              {Math.abs(cell.negotiatedPct).toFixed(1)}%
            </span>
          </Tooltip>
        ) : null}
        {cell.deltaPct != null && cell.deltaPct > 0 ? (
          <span className="text-[10px] opacity-80">
            {t("rfq.pricing.vsBest", {
              defaultValue: "+{{pct}}% vs 最低",
              pct: cell.deltaPct.toFixed(1),
            })}
          </span>
        ) : null}
        {showTarget && cell.vsTargetPct != null ? (
          <span
            className={`text-[10px] font-medium ${cell.vsTargetPct > 0 ? "text-red-500" : "text-emerald-600"}`}
          >
            {t("rfq.pricing.vsTarget", {
              defaultValue: "{{pct}}% vs 目標",
              pct: `${cell.vsTargetPct > 0 ? "+" : ""}${cell.vsTargetPct.toFixed(1)}`,
            })}
          </span>
        ) : null}
      </div>
    </td>
  );
}

function LineDetail({
  line,
  vendors,
  row,
}: {
  line: RfqLine;
  vendors: RfqVendor[];
  row: Record<string, Cell>;
}) {
  const { t } = useRfqLabels();
  const quoted = vendors.filter((vendor) => row[vendor.id]?.quote?.supplied);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-gray-500">
        {t("rfq.pricing.detailTitle") || "報價明細與議價歷程"}
      </span>
      {quoted.length === 0 ? (
        <p className="text-xs text-gray-500">
          {t("rfq.pricing.noQuote") || "本項尚無廠商報價。"}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
          {quoted.map((vendor) => {
            const cell = row[vendor.id] as Cell;
            const quote = cell.quote as RfqQuote;
            const prev = cell.prevQuote;
            const negotiated =
              cell.negotiatedPct != null && !!prev && prev.supplied;
            return (
              <div
                key={vendor.id}
                className="flex flex-col gap-1.5 rounded-lg border border-gray-200 bg-white p-3 text-xs"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-700">
                    {vendor.name}
                  </span>
                  {cell.rank === "best" ? (
                    <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-medium text-emerald-600">
                      <IconTrophy size={12} />
                      {t("rfq.pricing.best") || "最低"}
                    </span>
                  ) : null}
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>{t("rfq.pricing.quote") || "報價"}</span>
                  <span className="text-gray-700">
                    {fmtUnitPrice(quote.unitPrice)} 元/{quote.quoteUnit}
                  </span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>{t("rfq.pricing.converted") || "換算基準"}</span>
                  <span className="text-gray-700">
                    {cell.result?.base != null
                      ? `${fmtUnitPrice(cell.result.base)} ${
                          line.priceMode === "weight"
                            ? "元/kg"
                            : `元/${line.unit}`
                        }`
                      : t("rfq.pricing.cannotCompare") || "無法比價"}
                  </span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>{t("rfq.pricing.subtotal") || "小計"}</span>
                  <span className="text-gray-700">
                    {fmtMoney(cell.result?.subtotal ?? 0)} 元
                  </span>
                </div>
                <div className="flex justify-between gap-2 text-gray-500">
                  <span>{t("rfq.pricing.freight") || "運費"}</span>
                  <span className="text-right text-gray-700">
                    {quote.freightIncluded
                      ? t("rfq.pricing.freightIncluded") || "含運至工地"
                      : t("rfq.pricing.freightExcluded") ||
                        "出廠價（另計運費）"}
                  </span>
                </div>
                {quote.isAlternative ? (
                  <span className="w-fit rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-600">
                    {t("rfq.pricing.alternative") || "替代料"}・
                    {quote.altApproved
                      ? t("rfq.pricing.altApproved") || "已核准"
                      : t("rfq.pricing.altPending") || "待核准"}
                    {quote.altSpec ? `（${quote.altSpec}）` : ""}
                  </span>
                ) : null}
                <div className="mt-1 border-t border-gray-100 pt-1.5">
                  {negotiated ? (
                    <span className="inline-flex flex-wrap items-center gap-1 text-gray-500">
                      <IconHeartHandshake size={14} className="text-blue-500" />
                      {t("rfq.pricing.negotiation") || "議價"}：
                      {fmtUnitPrice((prev as RfqQuote).unitPrice)} →{" "}
                      {fmtUnitPrice(quote.unitPrice)} 元/{quote.quoteUnit}
                      <span
                        className={`font-medium ${
                          (cell.negotiatedPct as number) < 0
                            ? "text-emerald-600"
                            : "text-red-500"
                        }`}
                      >
                        {(cell.negotiatedPct as number) < 0 ? "↓" : "↑"}
                        {Math.abs(cell.negotiatedPct as number).toFixed(1)}%
                      </span>
                    </span>
                  ) : (
                    <span className="text-gray-500">
                      {t("rfq.pricing.firstQuote") || "初次報價，未議價"}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AwardRow({
  line,
  award,
  vendors,
  row,
  onChange,
  disabled,
}: {
  line: RfqLine;
  award: RfqAward;
  vendors: RfqVendor[];
  row: Record<string, Cell>;
  onChange: (patch: Partial<RfqAward>) => void;
  disabled: boolean;
}) {
  const { t, awardReason } = useRfqLabels();
  const suppliedVendors = vendors.filter(
    (vendor) => row[vendor.id]?.quote?.supplied,
  );
  const selected = award.vendorId ? row[award.vendorId] : undefined;

  const borderClass = award.skip
    ? "border-dashed border-gray-300 bg-gray-50"
    : award.vendorId
      ? "border-blue-200 bg-blue-50/40"
      : "border-gray-200 bg-white";

  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center ${borderClass}`}
    >
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <span className="mt-0.5 text-xs font-medium text-gray-500">
          {line.seq}
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-gray-700">
            {line.name}
          </span>
          <span className="text-xs text-gray-500">{line.spec}</span>
        </div>
      </div>

      {!award.skip ? (
        <div className="flex items-center gap-2">
          <Select
            size="xs"
            className="w-48"
            disabled={disabled}
            placeholder={t("rfq.award.selectVendor") || "選擇得標廠商"}
            value={award.vendorId}
            onChange={(value) => onChange({ vendorId: value })}
            data={suppliedVendors.map((vendor) => {
              const cell = row[vendor.id];
              const base = cell?.result?.base;
              return {
                value: vendor.id,
                label:
                  base != null
                    ? `${vendor.name}  ${fmtUnitPrice(base)}`
                    : vendor.name,
              };
            })}
            aria-label={`${line.name} ${t("rfq.award.selectVendor") || "選擇得標廠商"}`}
          />
          {selected?.result ? (
            <span className="w-28 text-right text-sm font-semibold tabular-nums text-gray-700">
              ${fmtMoney(selected.result.subtotal)}
            </span>
          ) : null}
        </div>
      ) : null /* (
        <Tooltip
          label={
            t("rfq.award.skipReasonDisabled") ||
            "後端未提供不採購原因欄位，暫時無法儲存"
          }
          withArrow
          multiline
          w={240}
        >
          <div className="opacity-60">
            <Select
              size="xs"
              disabled
              className="w-52"
              placeholder={t("rfq.award.skipReason") || "不採購原因"}
              value={null}
              data={AWARD_REASON_KEYS.map((key) => ({
                value: key,
                label: awardReason(key),
              }))}
              aria-label={`${line.name} ${t("rfq.award.skipReason") || "不採購原因"}`}
            />
          </div>
        </Tooltip>
      ) */}

      <Button
        size="xs"
        radius="md"
        variant={award.skip ? "light" : "subtle"}
        color="gray"
        leftSection={<IconBan size={14} />}
        disabled={disabled}
        onClick={() =>
          onChange({
            skip: !award.skip,
            vendorId: award.skip ? award.vendorId : null,
          })
        }
      >
        {award.skip
          ? t("rfq.award.resume") || "恢復採購"
          : t("rfq.award.skip") || "本次不採購"}
      </Button>
    </div>
  );
}

function RfqPricingTab({
  lines,
  role,
  vendors: allVendors,
  quotes: allQuotes,
  awards,
  onAwardsChange,
  editable,
  status,
  onSaveAwards,
  onSubmitForReview,
  onApprove,
  onReject,
}: {
  lines: RfqLine[];
  role: RfqRole;
  vendors: RfqVendor[];
  quotes: RfqQuote[];
  awards: RfqAward[];
  onAwardsChange: (awards: RfqAward[]) => void;
  editable: boolean;
  status: RfqStatus;
  onSaveAwards: (awards: RfqAward[]) => Promise<void> | void;
  onSubmitForReview: () => Promise<void> | void;
  onApprove: () => Promise<void> | void;
  onReject: (reason: string) => Promise<void> | void;
}) {
  const { t, category, calc } = useRfqLabels();
  const [settings, setSettings] = useState<CompareSettings>({
    tax: "excl",
    freight: "exw",
  });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [savingAwards, setSavingAwards] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const isManager = role === "manager";

  const { quotes, prevByKey } = useMemo(() => {
    const latest = new Map<string, RfqQuote>();
    const previous = new Map<string, RfqQuote>();
    for (const quote of allQuotes) {
      const key = `${quote.vendorId}|${quote.lineId}`;
      const current = latest.get(key);
      if (!current || quote.round > current.round) {
        if (current) previous.set(key, current);
        latest.set(key, quote);
      } else if (quote.round < current.round) {
        const prev = previous.get(key);
        if (!prev || quote.round > prev.round) previous.set(key, quote);
      }
    }
    return { quotes: Array.from(latest.values()), prevByKey: previous };
  }, [allQuotes]);

  const metas = useMemo<RfqQuoteMeta[]>(() => [], []);

  const vendors = useMemo(
    () =>
      allVendors.filter((vendor) =>
        quotes.some((quote) => quote.vendorId === vendor.id && quote.supplied),
      ),
    [allVendors, quotes],
  );

  const toggleExpand = (lineId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });

  const vendorSubtotals = useMemo(() => {
    const map: Record<string, number[]> = {};
    for (const vendor of vendors) {
      map[vendor.id] = quotes
        .filter((quote) => quote.vendorId === vendor.id && quote.supplied)
        .map((quote) => quote.quoteQty * quote.unitPrice);
    }
    return map;
  }, [vendors, quotes]);

  const grid = useMemo(() => {
    const result: Record<string, Record<string, Cell>> = {};
    for (const line of lines) {
      result[line.id] = {};
      const bases: { vendorId: string; base: number | null }[] = [];
      for (const vendor of vendors) {
        const quote = quotes.find(
          (item) => item.vendorId === vendor.id && item.lineId === line.id,
        );
        const prev = prevByKey.get(`${vendor.id}|${line.id}`);
        const negotiatedPct =
          prev && quote && prev.supplied && quote.supplied && prev.unitPrice > 0
            ? ((quote.unitPrice - prev.unitPrice) / prev.unitPrice) * 100
            : null;
        if (!quote || !quote.supplied) {
          result[line.id][vendor.id] = {
            quote,
            prevQuote: prev,
            negotiatedPct: null,
            rank: "none",
            deltaPct: null,
            vsTargetPct: null,
          };
          continue;
        }
        const meta = metas.find((item) => item.vendorId === vendor.id);
        const compared = computeCompare(
          line,
          quote,
          meta,
          vendorSubtotals[vendor.id] ?? [],
          settings,
          calc,
        );
        result[line.id][vendor.id] = {
          quote,
          prevQuote: prev,
          negotiatedPct,
          result: compared,
          rank: "warn",
          deltaPct: null,
          vsTargetPct: null,
        };
        bases.push({ vendorId: vendor.id, base: compared.base });
      }

      const valid = bases.filter((item) => item.base != null) as {
        vendorId: string;
        base: number;
      }[];
      if (valid.length) {
        const best = Math.min(...valid.map((item) => item.base));
        for (const item of valid) {
          const cell = result[line.id][item.vendorId];
          const deltaPct = ((item.base - best) / best) * 100;
          cell.deltaPct = deltaPct;
          if (line.targetPrice != null) {
            cell.vsTargetPct =
              ((item.base - line.targetPrice) / line.targetPrice) * 100;
          }
          if (item.base === best) cell.rank = "best";
          else if (deltaPct <= 3) cell.rank = "close";
          else if (line.targetPrice != null && item.base > line.targetPrice)
            cell.rank = "over";
          else cell.rank = "warn";
        }
      }
    }
    return result;
  }, [
    lines,
    vendors,
    quotes,
    metas,
    vendorSubtotals,
    settings,
    prevByKey,
    calc,
  ]);

  const setAward = (lineId: string, patch: Partial<RfqAward>) =>
    onAwardsChange(
      awards.map((award) =>
        award.lineId === lineId ? { ...award, ...patch } : award,
      ),
    );

  const autoAward = () =>
    onAwardsChange(
      awards.map((award) => {
        const row = grid[award.lineId];
        if (!row) return award;
        const best = Object.entries(row).find(
          ([, cell]) => cell.rank === "best",
        );
        return best ? { ...award, vendorId: best[0], skip: false } : award;
      }),
    );

  const awardTotal = useMemo(() => {
    let total = 0;
    for (const award of awards) {
      if (award.skip || !award.vendorId) continue;
      const cell = grid[award.lineId]?.[award.vendorId];
      if (cell?.result) total += cell.result.subtotal;
    }
    return total;
  }, [awards, grid]);

  const targetTotal = useMemo(() => {
    let total = 0;
    for (const line of lines) {
      if (line.targetPrice == null) continue;
      if (line.priceMode === "weight") {
        const weight = resolveWeight(line, calc);
        if (weight) total += line.targetPrice * weight.kg;
      } else {
        total += line.targetPrice * line.qty;
      }
    }
    return total;
  }, [lines, calc]);

  const decidedCount = awards.filter(
    (award) => award.vendorId || award.skip,
  ).length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-gray-500">
            {t("rfq.pricing.taxBasis") || "稅別基準"}
          </span>
          <SegmentedControl
            size="xs"
            value={settings.tax}
            onChange={(value) =>
              setSettings((prev) => ({
                ...prev,
                tax: value as CompareSettings["tax"],
              }))
            }
            data={[
              { value: "excl", label: t("rfq.pricing.taxExcl") || "未稅" },
              { value: "incl", label: t("rfq.pricing.taxIncl") || "含稅" },
            ]}
          />
        </div>

        {/* <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-gray-500">
            {t("rfq.pricing.freightBasis") || "運費基準"}
          </span>
          <SegmentedControl
            size="xs"
            value={settings.freight}
            onChange={(value) =>
              setSettings((prev) => ({
                ...prev,
                freight: value as CompareSettings["freight"],
              }))
            }
            data={[
              { value: "exw", label: t("rfq.pricing.freightExw") || "出廠" },
              {
                value: "dap",
                label: t("rfq.pricing.freightDap") || "含運至工地",
                disabled: true,
              },
            ]}
          />
          <span className="text-[10px] text-gray-400">
            {t("rfq.pricing.freightDapDisabled") ||
              "後端未提供廠商運費總額，暫不支援運費分攤"}
          </span>
        </div> */}

        {isManager ? (
          <Button
            size="xs"
            radius="md"
            variant="light"
            color="blue"
            className="ml-auto"
            leftSection={<IconTrophy size={14} />}
            disabled={!editable}
            onClick={autoAward}
          >
            {t("rfq.pricing.autoAward") || "一鍵帶入最低價"}
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1 text-xs text-gray-500">
        <LegendDot
          kind="best"
          label={t("rfq.pricing.legendBest") || "最低價"}
        />
        <LegendDot
          kind="close"
          label={t("rfq.pricing.legendClose") || "≤3% 接近"}
        />
        {isManager ? (
          <LegendDot
            kind="over"
            label={t("rfq.pricing.legendOver") || "超過目標價"}
          />
        ) : null}
        <span className="inline-flex items-center gap-1">
          <span className="rounded bg-emerald-100/70 px-1 font-medium text-emerald-700">
            {t("rfq.pricing.negotiatedTag") || "議價"} ↓
          </span>
          {t("rfq.pricing.legendNegotiated") || "已議價降幅"}
        </span>
        <span className="inline-flex items-center gap-1">
          <IconCircleOff size={14} />
          {t("rfq.pricing.legendMissing") || "缺項/未報"}
        </span>
        <span className="inline-flex items-center gap-1">
          <IconInfoCircle size={14} />
          {t("rfq.pricing.legendFormula") || "指向單價看換算公式"}
        </span>
        <span className="inline-flex items-center gap-1">
          <IconChevronRight size={14} />
          {t("rfq.pricing.legendExpand") || "點項次展開報價明細與議價歷程"}
        </span>
      </div>

      <RfqSection
        icon={IconTable}
        title={t("rfq.pricing.tableTitle") || "比價表"}
        bodyClassName="p-0"
      >
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr className="text-left">
                <th className="sticky left-0 z-10 min-w-[220px] border-b border-gray-200 bg-white px-3 py-2.5 font-medium text-gray-500">
                  {t("rfq.pricing.colLine") || "項次 / 品名規格"}
                </th>
                <th className="border-b border-gray-200 px-3 py-2.5 text-right font-medium text-gray-500">
                  {t("rfq.pricing.colBase") || "基準"}
                </th>
                {isManager ? (
                  <th className="border-b border-gray-200 px-3 py-2.5 text-right font-medium text-gray-500">
                    {t("rfq.pricing.colTarget") || "目標單價"}
                  </th>
                ) : null}
                {vendors.map((vendor) => (
                  <th
                    key={vendor.id}
                    className="min-w-[150px] border-b border-gray-200 px-3 py-2.5 text-right font-medium text-gray-700"
                  >
                    {vendor.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const row = grid[line.id] ?? {};
                const baseUnit =
                  line.priceMode === "weight" ? "元/kg" : `元/${line.unit}`;
                const isOpen = expanded.has(line.id);
                const columnCount = 2 + (isManager ? 1 : 0) + vendors.length;
                return (
                  <Fragment key={line.id}>
                    <tr>
                      <td className="sticky left-0 z-10 border-b border-gray-100 bg-white px-3 py-2.5 align-top">
                        <button
                          type="button"
                          onClick={() => toggleExpand(line.id)}
                          aria-expanded={isOpen}
                          className="flex w-full items-start gap-2 text-left"
                        >
                          <span className="mt-0.5 flex items-center gap-1">
                            {isOpen ? (
                              <IconChevronDown
                                size={14}
                                className="text-gray-400"
                              />
                            ) : (
                              <IconChevronRight
                                size={14}
                                className="text-gray-400"
                              />
                            )}
                            <span className="text-xs font-medium text-gray-500">
                              {line.seq}
                            </span>
                          </span>
                          <span className="flex flex-col gap-0.5">
                            <span className="font-medium text-gray-700">
                              {line.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {line.spec}
                            </span>
                            <span className="mt-1 w-fit rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-500">
                              {category(line.category)}
                            </span>
                          </span>
                        </button>
                      </td>
                      <td className="border-b border-gray-100 px-3 py-2.5 text-right align-top text-xs text-gray-500">
                        {baseUnit}
                        <div className="text-[10px]">
                          {line.qty} {line.unit}
                        </div>
                      </td>
                      {isManager ? (
                        <td className="border-b border-gray-100 px-3 py-2.5 text-right align-top">
                          {line.targetPrice != null ? (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                              <IconTarget size={12} />
                              {fmtUnitPrice(line.targetPrice)}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      ) : null}
                      {vendors.map((vendor) => (
                        <PriceCell
                          key={vendor.id}
                          cell={row[vendor.id]}
                          baseUnit={baseUnit}
                          showTarget={isManager && line.targetPrice != null}
                        />
                      ))}
                    </tr>
                    {isOpen ? (
                      <tr>
                        <td
                          colSpan={columnCount}
                          className="border-b border-gray-100 bg-gray-50/60 px-4 py-3"
                        >
                          <LineDetail line={line} vendors={vendors} row={row} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
              <tr className="bg-gray-50 font-medium">
                <td className="sticky left-0 z-10 bg-gray-50 px-3 py-2.5 text-gray-500">
                  {t("rfq.pricing.vendorTotal", {
                    defaultValue: "廠商調整後總額（{{tax}}・{{freight}}）",
                    tax:
                      settings.tax === "incl"
                        ? t("rfq.pricing.taxIncl") || "含稅"
                        : t("rfq.pricing.taxExcl") || "未稅",
                    freight:
                      settings.freight === "dap"
                        ? t("rfq.calc.freightIncluded") || "含運"
                        : t("rfq.pricing.freightExw") || "出廠",
                  })}
                </td>
                <td className="px-3 py-2.5" />
                {isManager ? <td className="px-3 py-2.5" /> : null}
                {vendors.map((vendor) => {
                  const total = lines.reduce((sum, line) => {
                    const cell = grid[line.id]?.[vendor.id];
                    return sum + (cell?.result?.subtotal ?? 0);
                  }, 0);
                  return (
                    <td
                      key={vendor.id}
                      className="px-3 py-2.5 text-right tabular-nums text-gray-700"
                    >
                      {fmtMoney(total)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </RfqSection>

      <RfqSection
        icon={IconAward}
        title={t("rfq.award.sectionTitle") || "決標區"}
        action={
          <span className="text-xs text-gray-500">
            {t("rfq.award.decided", {
              defaultValue: "已決定 {{decided}}/{{total}} 項",
              decided: decidedCount,
              total: lines.length,
            })}
          </span>
        }
      >
        {!isManager ? (
          <div className="flex items-center gap-2 rounded-lg bg-gray-50 p-4 text-sm text-gray-500">
            <IconAlertTriangle size={16} />
            {t("rfq.award.buyerNotice") ||
              "決標由採購主管執行，承辦僅可檢視比價結果。"}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {lines.map((line) => {
              const award = awards.find((item) => item.lineId === line.id);
              if (!award) return null;
              return (
                <AwardRow
                  key={line.id}
                  line={line}
                  award={award}
                  vendors={vendors}
                  row={grid[line.id] ?? {}}
                  onChange={(patch) => setAward(line.id, patch)}
                  disabled={!editable}
                />
              );
            })}

            <div className="mt-2 grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:grid-cols-3">
              <SummaryStat
                label={t("rfq.award.total") || "決標總額（未稅）"}
                value={`$${fmtMoney(awardTotal)}`}
                strong
              />
              <SummaryStat
                label={t("rfq.award.targetTotal") || "目標金額"}
                value={`$${fmtMoney(targetTotal)}`}
              />
              <SummaryStat
                label={t("rfq.award.diff") || "與目標差異"}
                value={`${awardTotal - targetTotal >= 0 ? "+" : ""}${fmtMoney(
                  awardTotal - targetTotal,
                )}`}
                tone={awardTotal <= targetTotal ? "good" : "bad"}
              />
            </div>

            {status === "pendingReview" ? (
              <div className="flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                <span className="text-sm text-amber-700">
                  {t("rfq.award.reviewNotice") ||
                    "此詢價單審核中，可核准或駁回。"}
                </span>
                <div className="flex flex-wrap items-end gap-2">
                  <TextInput
                    size="xs"
                    className="min-w-[240px] flex-1"
                    value={rejectReason}
                    placeholder={t("rfq.award.rejectReason") || "駁回原因"}
                    onChange={(event) =>
                      setRejectReason(event.currentTarget.value)
                    }
                    aria-label={t("rfq.award.rejectReason") || "駁回原因"}
                  />
                  <Button
                    size="xs"
                    radius="md"
                    color="red"
                    variant="light"
                    loading={submitting}
                    disabled={!rejectReason.trim()}
                    onClick={async () => {
                      setSubmitting(true);
                      try {
                        await onReject(rejectReason.trim());
                        setRejectReason("");
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {t("rfq.award.reject") || "駁回"}
                  </Button>
                  <Button
                    size="xs"
                    radius="md"
                    color="blue"
                    loading={submitting}
                    leftSection={<IconCircleCheck size={14} />}
                    onClick={async () => {
                      setSubmitting(true);
                      try {
                        await onApprove();
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {t("rfq.award.approve") || "核准"}
                  </Button>
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <Button
                size="xs"
                radius="md"
                variant="default"
                loading={savingAwards}
                disabled={!editable}
                onClick={async () => {
                  setSavingAwards(true);
                  try {
                    await onSaveAwards(awards);
                  } finally {
                    setSavingAwards(false);
                  }
                }}
              >
                {t("rfq.award.save") || "儲存決標"}
              </Button>
              <Button
                size="xs"
                radius="md"
                color="blue"
                loading={submitting}
                disabled={
                  !editable ||
                  lines.length === 0 ||
                  decidedCount < lines.length
                }
                leftSection={<IconCircleCheck size={14} />}
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    await onSaveAwards(awards);
                    await onSubmitForReview();
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {t("rfq.award.submit") || "送出審核"}
              </Button>
            </div>
          </div>
        )}
      </RfqSection>
    </div>
  );
}

export default RfqPricingTab;
